import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ROLE_COLORS } from "@/lib/palettes";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLES = ["admin", "manager", "executive"];

export default function AdminPanel({ open, onClose, users, online = [] }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "executive" });

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added to the crew ✦`);
      setForm({ name: "", email: "", password: "", role: "executive" });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}`, { role });
      toast.success("Role updated");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-user-management" className="max-w-lg rounded-2xl bg-card/95 backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">Crew Management 🛸</DialogTitle>
          <DialogDescription>Create teammates and manage their roles.</DialogDescription>
        </DialogHeader>

        <form onSubmit={createUser} className="space-y-2 rounded-xl border border-border bg-secondary/50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-bold"><UserPlus className="h-4 w-4 text-violet-500" /> Add teammate</div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="admin-new-name-input" required placeholder="Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none focus:border-violet-400" />
            <input data-testid="admin-new-email-input" required type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none focus:border-violet-400" />
            <input data-testid="admin-new-password-input" required minLength={6} type="password" placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none focus:border-violet-400" />
            <select data-testid="admin-new-role-select" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none cursor-pointer">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button data-testid="admin-create-user-button" type="submit"
            className="w-full rounded-lg bg-gradient-to-r from-violet-500 to-pink-500 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.01] active:scale-95">
            Create user
          </button>
        </form>

        <div className="max-h-64 space-y-1.5 overflow-y-auto">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2" data-testid={`admin-user-row-${u.email.split("@")[0]}`}>
              <div className="relative">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-pink-400 text-sm font-bold text-white">
                  {(u.name || "?")[0].toUpperCase()}
                </div>
                {online.includes(u.id) && (
                  <span data-testid={`admin-online-dot-${u.email.split("@")[0]}`} title="Online now"
                    className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-card" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{u.name}</div>
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
              </div>
              <select data-testid={`admin-role-select-${u.email.split("@")[0]}`} value={u.role}
                onChange={(e) => changeRole(u.id, e.target.value)}
                className={`rounded-full border px-2 py-0.5 text-xs font-mono uppercase outline-none cursor-pointer ${ROLE_COLORS[u.role]}`}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button data-testid={`admin-delete-user-${u.email.split("@")[0]}`} onClick={() => removeUser(u)}
                className="rounded-lg p-1.5 text-red-500 transition-colors hover:bg-red-500/10">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
